import type React from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { useQuery } from '@apollo/client';

import Typography from 'app/ui/components/atoms/typography';
import GradientActionCard from 'app/ui/components/molecules/gradient-action-card';
import Grid from 'app/ui/components/molecules/grid';
import { routes } from 'app/routes/constants';
import { TrainingPlans as TRAINING_PLANS } from 'app/pages/training-plans/query.gql';
import type { TrainingPlansQuery, TrainingPlansQueryVariables } from 'app/types/graphql';

const MoreContentWrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing(20)};
`;

const PlaybookTitle = styled(Typography)`
  margin-bottom: ${({ theme }) => theme.spacing(6)};
`;

interface MoreTrainingPlansProps {
  currentPlanId?: string;
}

const MoreTrainingPlans: React.FC<MoreTrainingPlansProps> = ({ currentPlanId }) => {
  const intl = useIntl();
  const { data, loading, error } = useQuery<TrainingPlansQuery, TrainingPlansQueryVariables>(TRAINING_PLANS);

  if (loading || error || !data) {
    return null;
  }

  const plans = (data.allProgressivePlans?.nodes || []).filter((plan) => plan.id !== currentPlanId);

  if (plans.length === 0) {
    return null;
  }

  return (
    <MoreContentWrapper>
      <PlaybookTitle variant="h3">
        {intl.formatMessage({ id: 'progressivePlan.more', defaultMessage: 'More training plans' })}
      </PlaybookTitle>
      <Grid elementsPerRow={2}>
        {plans.map((plan) => (
          <GradientActionCard
            key={plan.id}
            title={plan.name}
            shortDescription={plan.longDescription || ''}
            ctaImageUrl={plan.ctaImage?.signedUrl || plan.ctaImage?.url}
            to={routes.TRAINING_PLAN.replace(':id', plan.id)}
            size="large"
          />
        ))}
      </Grid>
    </MoreContentWrapper>
  );
};

export default MoreTrainingPlans;
